import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Search, ShieldAlert, UserCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Chip, DecisionChip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import OnboardingQueue from "@/pages/OnboardingQueue";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { RecordActions } from "@/components/common/RecordActions";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRY_NAME, money, shortDate } from "@/lib/format";

const BLANK = {
  name: "", donor_type: "individual", email: "", phone: "", country: "IN", pan_or_tax_id: "",
  is_anonymous: false, kyc_verified: false, sanctions_screened: false, sanctions_hit: false,
  pep: false, risk_score: 0, giving_modes: [], notes: "",
};

function riskTone(score) {
  if (score >= 70) return "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";
  if (score >= 40) return "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
  return "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
}

export default function Donors() {
  const { can } = useAuth();
  const [donors, setDonors] = useState(null);
  const rowDetail = useRowDetail("donor", () => load());
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => api.get("/charity/donors").then((r) => setDonors(r.data)), []);
  useEffect(() => { load().catch(() => setDonors([])); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/charity/donors", form);
      toast.success("Donor record created");
      setOpen(false);
      setForm(BLANK);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/charity/donors/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!donors) return <Loading label="Loading donor master" />;
  const filtered = donors.filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase()));
  const flagged = donors.filter((d) => d.sanctions_hit || d.risk_score >= 70).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-004 · Donor 360"
        title="Donor master, giving history and AML posture"
        description="Individual, corporate, institutional, anonymous and recurring profiles with KYC, sanctions screening and risk scoring feeding directly into the decision engine."
        actions={can("donor", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-donor-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> New donor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-border bg-card">
              <DialogHeader><DialogTitle>Create donor record</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Name" full>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                         data-testid="donor-name-input" className="bg-muted/40" />
                </F>
                <F label="Donor type">
                  <Select value={form.donor_type} onValueChange={(v) => setForm({ ...form, donor_type: v })}>
                    <SelectTrigger data-testid="donor-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["individual", "corporate", "institutional", "anonymous"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Country">
                  <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
                         data-testid="donor-country-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Email">
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                         data-testid="donor-email-input" className="bg-muted/40" />
                </F>
                <F label="PAN / Tax ID">
                  <Input value={form.pan_or_tax_id} onChange={(e) => setForm({ ...form, pan_or_tax_id: e.target.value })}
                         data-testid="donor-taxid-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Risk score (0-100)">
                  <Input type="number" value={form.risk_score}
                         onChange={(e) => setForm({ ...form, risk_score: Number(e.target.value) })}
                         data-testid="donor-risk-input" className="bg-muted/40 font-mono" />
                </F>
                <div className="col-span-2 space-y-2 border-t border-border pt-3">
                  {[["kyc_verified", "KYC verified"], ["sanctions_screened", "Sanctions screened"],
                    ["sanctions_hit", "Sanctions / PEP hit"], ["is_anonymous", "Anonymous donor"]].map(([k, l]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{l}</span>
                      <Switch checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })}
                              data-testid={`donor-toggle-${k}`} />
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={save} disabled={busy || !form.name} data-testid="donor-save-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save donor
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <Tabs defaultValue="register">
        <TabsList>
          <TabsTrigger value="register" data-testid="tab-donor-register">Donors Register</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-donor-onboarding">
            Invites, review queue & portal signups
          </TabsTrigger>
        </TabsList>

        <TabsContent value="register" className="mt-4 space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-donor-count" label="Donor records" value={donors.length} accent="sky" />
        <Kpi testId="kpi-donor-given" label="Lifetime giving" accent="emerald"
             value={money(donors.reduce((a, d) => a + (d.total_given || 0), 0), "INR", true)} />
        <Kpi testId="kpi-donor-kyc" label="KYC verified" accent="violet" icon={UserCheck}
             value={`${donors.filter((d) => d.kyc_verified).length}/${donors.length}`} />
        <Kpi testId="kpi-donor-flagged" label="High risk / sanctioned" value={flagged} accent="red" icon={ShieldAlert} />
      </div>

      <Panel testId="panel-donors" kicker="Master file" title="Donors"
             actions={
               <div className="relative">
                 <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                 <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search donors"
                        data-testid="donor-search-input" className="h-8 w-48 bg-muted/40 pl-8 text-xs" />
               </div>
             }>
        <Table testId="donors-table"
               head={<><Th>Donor</Th><Th>Type</Th><Th>Jurisdiction</Th><Th>Lifetime giving</Th><Th>Gifts</Th><Th>Screening</Th><Th>Risk</Th></>}>
          {filtered.length === 0 && <Empty colSpan={7} label="No donors match" />}
          {filtered.map((d) => (
            <tr key={d.id} className="row-hover cursor-pointer" data-testid={`donor-row-${d.id}`}
                onClick={() => openDetail(d.id)}>
              <Td>
                <div className="font-medium text-foreground">{d.name}</div>
                <div className="text-[11px] text-muted-foreground">{d.email || (d.is_anonymous ? "anonymous" : "—")}</div>
              </Td>
              <Td><Chip className="border-border bg-muted text-foreground/80">{d.donor_type}</Chip></Td>
              <Td>
                <span className="font-mono text-xs text-primary">{d.country}</span>
                <span className="ml-1.5 text-[11px] text-muted-foreground">{COUNTRY_NAME[d.country] || ""}</span>
              </Td>
              <Td className="font-mono">{money(d.total_given, "INR", true)}</Td>
              <Td className="font-mono">{d.donation_count}</Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {d.kyc_verified && <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">KYC</Chip>}
                  {d.sanctions_screened && <Chip className="border-border bg-muted text-foreground/80">Screened</Chip>}
                  {d.sanctions_hit && <Chip className="border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300">Sanctions hit</Chip>}
                  {d.pep && <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">PEP</Chip>}
                </div>
              </Td>
              <Td><Chip className={riskTone(d.risk_score)} testId={`donor-risk-${d.id}`}>{d.risk_score}</Chip></Td>
            </tr>
          ))}
        </Table>
      </Panel>
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          <OnboardingQueue embedded onAccepted={() => load()} />
        </TabsContent>
      </Tabs>

      <Sheet open={Boolean(detail)} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto border-border bg-card sm:max-w-xl">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-foreground" data-testid="donor-detail-name">{detail.donor.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-3">
                <RecordActions resource="donor" record={detail.donor}
                               onDone={async (deleted) => {
                                 if (deleted) setDetail(null);
                                 await load();
                               }} />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <Kpi testId="donor-detail-total" label="Lifetime giving" value={money(detail.donor.total_given, "INR", true)} accent="emerald" />
                <Kpi testId="donor-detail-count" label="Gifts recorded" value={detail.donor.donation_count} accent="sky" />
              </div>
              <div className="mt-4 space-y-1.5 rounded border border-border p-3 text-xs">
                <Line k="Type" v={detail.donor.donor_type} />
                <Line k="Jurisdiction" v={`${detail.donor.country} · ${COUNTRY_NAME[detail.donor.country] || ""}`} />
                <Line k="Tax ID" v={detail.donor.pan_or_tax_id || "—"} />
                <Line k="Risk score" v={detail.donor.risk_score} />
                <Line k="Giving modes" v={(detail.donor.giving_modes || []).join(", ") || "—"} />
                {detail.donor.notes && <Line k="Notes" v={detail.donor.notes} />}
              </div>
              <div className="mt-5">
                <div className="kicker mb-2">Giving history</div>
                <Table testId="donor-history-table" head={<><Th>Date</Th><Th>Reference</Th><Th>Fund</Th><Th>Amount</Th><Th>Decision</Th></>}>
                  {detail.donations.length === 0 && <Empty colSpan={5} label="No gifts recorded" />}
                  {detail.donations.map((g) => (
                    <tr key={refOf(g)} className="row-hover cursor-pointer" onClick={rowClick(() => rowDetail.show(g, refOf(g), "", "", "donation"))}>
                      <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(g.donation_date)}</Td>
                      <Td className="font-mono text-[11px] text-primary">{g.reference}</Td>
                      <Td className="font-mono text-[11px]">{g.fund_code}</Td>
                      <Td className="font-mono">{money(g.amount, g.currency)}</Td>
                      <Td><DecisionChip decision={g.decision} /></Td>
                    </tr>
                  ))}
                </Table>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {rowDetail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Line = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{k}</span>
    <span className="text-right text-foreground/80">{String(v)}</span>
  </div>
);
